// Amazon penalises a seller for dispatching after the Latest Ship Date, and
// nothing in the Orders list said so — CP#3706 went out three days late and
// looked no different from any other row. The deadline is the merchant's, not
// the carrier's, so it is shown as time remaining rather than as a date they
// have to subtract from today in their head.

const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

// Inside a day the merchant is deciding whether to pack it now, so hours are
// the useful unit. Beyond that the date is.
const URGENT_WITHIN_HOURS = 24;

export interface ShipByBadge {
  label: string;
  tone: "success" | "warning" | "critical";
}

function formatDeadline(deadline: Date): string {
  return deadline.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

export function shipByBadge(latestShipDate: string | null, now: Date): ShipByBadge | null {
  if (!latestShipDate) return null;
  const deadline = new Date(latestShipDate);
  if (Number.isNaN(deadline.getTime())) return null;

  const remainingMs = deadline.getTime() - now.getTime();
  if (remainingMs < 0) {
    // Rounded up: an order eight hours late is already a missed deadline, and
    // calling that "0d late" reads as if it were still fine.
    const daysLate = Math.max(1, Math.ceil(-remainingMs / MS_PER_DAY));
    return { label: `OVERDUE — ${daysLate}d late`, tone: "critical" };
  }

  const remainingHours = remainingMs / MS_PER_HOUR;
  if (remainingHours < URGENT_WITHIN_HOURS) {
    return { label: `Ship soon — ${Math.round(remainingHours)}h`, tone: "warning" };
  }

  return { label: `Ship by ${formatDeadline(deadline)}`, tone: "success" };
}

// Orders with no Amazon deadline sort last rather than first: they are not
// urgent, and floating them above a contractual deadline would defeat the sort.
export function compareShipByUrgency(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a < b ? -1 : 1;
}
